import { useState, useEffect } from "react";

const UploadEmployeeManagementData = ({ setShowUploadPopup }) => {
  const accessToken = localStorage.getItem("accessToken");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [hasUploadPermission, setHasUploadPermission] = useState(false);

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("userPermissions") || "[]");
    const hasPermission = permissions.some(
      (permission) =>
        permission.name === "view_per_group_all_employees_info" ||
        permission.name === "view_all_managed_employees_info"
    );
    setHasUploadPermission(hasPermission);

    if (!hasPermission) {
      setMessage({
        type: "error",
        text: "You don't have permission to upload employee data.",
      });
    }
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setMessage({ type: "", text: "" });

    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

      if (!["xlsx", "xls"].includes(fileExtension)) {
        setMessage({
          type: "error",
          text: "Invalid file format. Please upload an Excel file (.xlsx or .xls)",
        });
        setFile(null);
        e.target.value = "";
        return;
      }

      setFile(selectedFile);
      setMessage({
        type: "info",
        text: `Selected: ${selectedFile.name}`,
      });
    }
  };

  const handleUpload = async () => {
    if (!hasUploadPermission) {
      setMessage({
        type: "error",
        text: "You don't have permission to upload employee data.",
      });
      return;
    }

    if (!file) {
      setMessage({
        type: "error",
        text: "Please select a file first",
      });
      return;
    }

    setUploading(true);
    setMessage({ type: "", text: "" });

    const myHeaders = new Headers();
    myHeaders.append("Accept", "application/json");
    myHeaders.append("Authorization", `Bearer ${accessToken}`);

    const formData = new FormData();
    formData.append("file", file);

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formData,
      redirect: "follow",
    };

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/BulkDataUpload`,
        requestOptions
      );
      const result = await response.text();

      let data;
      try {
        data = JSON.parse(result);
      } catch {
        data = { message: result };
      }

      if (response.ok) {
        setMessage({
          type: "success",
          text: data.message || "File uploaded successfully!",
        });
        setFile(null);

        document.getElementById("fileInput").value = "";

        setTimeout(() => {
          setShowUploadPopup(false);
        }, 2000);
      } else {
        setMessage({
          type: "error",
          text:
            data.error || data.message || "Upload failed. Please try again.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error.message ||
          "Network error. Please check your connection and try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            Upload Employee Data
          </h2>
          <button
            onClick={() => setShowUploadPopup(false)}
            className="text-gray-600 hover:text-red-500 text-xl font-bold hover:scale-105 transition-transform"
            disabled={uploading}
          >
            ✖️
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Excel File (.xlsx, .xls)
            </label>
            <input
              id="fileInput"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={uploading || !hasUploadPermission}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          {message.text && (
            <div
              className={`p-3 rounded-md text-sm ${
                message.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : message.type === "error"
                  ? "bg-red-50 text-red-800 border border-red-200"
                  : "bg-blue-50 text-blue-800 border border-blue-200"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              onClick={handleUpload}
              disabled={!file || uploading || !hasUploadPermission}
              className="flex-1 bg-primary-hrms text-white py-2 px-4 rounded-md hover:bg-primary-hrms/80 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {uploading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  Uploading...
                </span>
              ) : (
                "Upload"
              )}
            </button>
            <button
              onClick={() => setShowUploadPopup(false)}
              disabled={uploading}
              className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadEmployeeManagementData;